/**
 * VistoeraTASK-00604 — inject public software-source metadata into the
 * FINAL editor export bytes (post-canvas.toBlob, pre-upload) without
 * re-encoding pixels and without drawing any visible watermark.
 *
 * Privacy: only public brand strings. Never email/tenant/asset/R2/URLs
 * that identify a user or internal environment.
 */

export const VISTOERA_SOFTWARE = 'Vistoera'
export const VISTOERA_DESCRIPTION = 'Edited with Vistoera'
export const VISTOERA_SOURCE_URL = 'https://vistoera.com'

/** PNG keyword → value (standard tEXt keywords only). */
export const VISTOERA_PNG_TEXT: ReadonlyArray<readonly [string, string]> = [
  ['Software', VISTOERA_SOFTWARE],
  ['Description', VISTOERA_DESCRIPTION],
  // PNG standard "Source" keyword — used here for public product URL.
  ['Source', VISTOERA_SOURCE_URL],
]

const PNG_SIG = new Uint8Array([137, 80, 78, 71, 13, 10, 26, 10])

function toArrayBuffer(u8: Uint8Array): ArrayBuffer {
  return u8.buffer.slice(u8.byteOffset, u8.byteOffset + u8.byteLength) as ArrayBuffer
}

function isPng(bytes: Uint8Array): boolean {
  if (bytes.length < 8) return false
  for (let i = 0; i < 8; i++) if (bytes[i] !== PNG_SIG[i]) return false
  return true
}

function isJpeg(bytes: Uint8Array): boolean {
  return bytes.length >= 2 && bytes[0] === 0xff && bytes[1] === 0xd8
}

/** CRC-32 (ISO 3309 / PNG) over type+data. */
function crc32(buf: Uint8Array): number {
  let c = 0xffffffff
  for (let i = 0; i < buf.length; i++) {
    c ^= buf[i]
    for (let k = 0; k < 8; k++) {
      c = c & 1 ? (0xedb88320 ^ (c >>> 1)) : c >>> 1
    }
  }
  return (c ^ 0xffffffff) >>> 0
}

function concatBytes(parts: Uint8Array[]): Uint8Array {
  let len = 0
  for (const p of parts) len += p.length
  const out = new Uint8Array(len)
  let o = 0
  for (const p of parts) {
    out.set(p, o)
    o += p.length
  }
  return out
}

function encodeLatin1(s: string): Uint8Array {
  const out = new Uint8Array(s.length)
  for (let i = 0; i < s.length; i++) {
    const code = s.charCodeAt(i)
    if (code > 0xff) {
      throw new Error(`PNG tEXt requires Latin-1; got codepoint U+${code.toString(16)}`)
    }
    out[i] = code
  }
  return out
}

function buildPngTextChunk(keyword: string, text: string): Uint8Array {
  const type = encodeLatin1('tEXt')
  const data = concatBytes([encodeLatin1(keyword), new Uint8Array([0]), encodeLatin1(text)])
  const len = new Uint8Array(4)
  new DataView(len.buffer).setUint32(0, data.length, false)
  const typeAndData = concatBytes([type, data])
  const crc = new Uint8Array(4)
  new DataView(crc.buffer).setUint32(0, crc32(typeAndData), false)
  return concatBytes([len, typeAndData, crc])
}

/**
 * Insert Vistoera tEXt chunks immediately after IHDR.
 * Does not touch IDAT / IHDR payloads (pixels + Alpha unchanged).
 */
export function injectPngTextMetadata(bytes: Uint8Array): Uint8Array {
  if (!isPng(bytes)) throw new Error('injectPngTextMetadata: not a PNG')
  // Walk chunks: signature(8) + length(4)+type(4)+data+crc(4)
  let off = 8
  if (off + 8 > bytes.length) throw new Error('injectPngTextMetadata: truncated PNG')
  const ihdrLen = new DataView(bytes.buffer, bytes.byteOffset + off, 4).getUint32(0, false)
  const ihdrType = String.fromCharCode(bytes[off + 4], bytes[off + 5], bytes[off + 6], bytes[off + 7])
  if (ihdrType !== 'IHDR') throw new Error('injectPngTextMetadata: IHDR missing')
  const afterIhdr = off + 12 + ihdrLen
  if (afterIhdr > bytes.length) throw new Error('injectPngTextMetadata: truncated IHDR')

  // Drop any prior Software/Description/Source tEXt we may re-inject
  // (idempotent on retry) while keeping every other chunk intact.
  const kept: Uint8Array[] = [bytes.subarray(0, afterIhdr)]
  const skipKeys = new Set(VISTOERA_PNG_TEXT.map(([k]) => k))
  off = afterIhdr
  while (off + 8 <= bytes.length) {
    const len = new DataView(bytes.buffer, bytes.byteOffset + off, 4).getUint32(0, false)
    const type = String.fromCharCode(bytes[off + 4], bytes[off + 5], bytes[off + 6], bytes[off + 7])
    const chunkEnd = off + 12 + len
    if (chunkEnd > bytes.length) throw new Error('injectPngTextMetadata: truncated chunk')
    if (type === 'tEXt') {
      const data = bytes.subarray(off + 8, off + 8 + len)
      const nul = data.indexOf(0)
      const key = nul >= 0 ? String.fromCharCode(...data.subarray(0, nul)) : ''
      if (skipKeys.has(key)) {
        off = chunkEnd
        continue
      }
    }
    kept.push(bytes.subarray(off, chunkEnd))
    off = chunkEnd
    if (type === 'IEND') break
  }
  if (off !== bytes.length && off < bytes.length) {
    // Trailing bytes after IEND are illegal for us — reject rather than
    // silently produce a suspicious file.
    throw new Error('injectPngTextMetadata: unexpected trailing bytes')
  }

  const textChunks = VISTOERA_PNG_TEXT.map(([k, v]) => buildPngTextChunk(k, v))
  // Rebuild: sig+IHDR + our tEXt + remaining kept (everything after IHDR)
  return concatBytes([kept[0], ...textChunks, ...kept.slice(1)])
}

/** Read PNG tEXt keyword→value map (Latin-1). */
export function readPngTextMetadata(bytes: Uint8Array): Record<string, string> {
  if (!isPng(bytes)) throw new Error('readPngTextMetadata: not a PNG')
  const out: Record<string, string> = {}
  let off = 8
  while (off + 8 <= bytes.length) {
    const len = new DataView(bytes.buffer, bytes.byteOffset + off, 4).getUint32(0, false)
    const type = String.fromCharCode(bytes[off + 4], bytes[off + 5], bytes[off + 6], bytes[off + 7])
    const chunkEnd = off + 12 + len
    if (chunkEnd > bytes.length) break
    if (type === 'tEXt') {
      const data = bytes.subarray(off + 8, off + 8 + len)
      const nul = data.indexOf(0)
      if (nul > 0) {
        const key = String.fromCharCode(...data.subarray(0, nul))
        const val = String.fromCharCode(...data.subarray(nul + 1))
        out[key] = val
      }
    }
    off = chunkEnd
    if (type === 'IEND') break
  }
  return out
}

function buildJpegCom(text: string): Uint8Array {
  const payload = encodeLatin1(text)
  // COM: FF FE + length(2, includes these 2 bytes) + data
  const len = payload.length + 2
  if (len > 0xffff) throw new Error('JPEG COM too large')
  const out = new Uint8Array(2 + 2 + payload.length)
  out[0] = 0xff
  out[1] = 0xfe
  out[2] = (len >> 8) & 0xff
  out[3] = len & 0xff
  out.set(payload, 4)
  return out
}

/**
 * Minimal TIFF/EXIF APP1 with IFD0 Software (0x0131) + ImageDescription (0x010E).
 * Little-endian. No GPS / MakerNote / user PII.
 */
function buildJpegExifApp1(software: string, description: string): Uint8Array {
  const soft = encodeLatin1(software + '\0')
  const desc = encodeLatin1(description + '\0')

  // Layout after "Exif\0\0":
  //   0: II 0x2A 0x00000008 (IFD0 offset)
  //   8: IFD0 entry count + 2 entries + next IFD=0
  //   then value payloads (Software, ImageDescription) when count>4
  const ifdEntryCount = 2
  const ifdSize = 2 + ifdEntryCount * 12 + 4
  const softOffset = 8 + ifdSize
  const descOffset = softOffset + soft.length
  const tiffLen = descOffset + desc.length

  const tiff = new Uint8Array(tiffLen)
  const dv = new DataView(tiff.buffer)
  // TIFF header
  tiff[0] = 0x49
  tiff[1] = 0x49 // II
  dv.setUint16(2, 42, true)
  dv.setUint32(4, 8, true) // IFD0 at 8

  let p = 8
  dv.setUint16(p, ifdEntryCount, true)
  p += 2

  // ImageDescription 0x010E — ASCII
  dv.setUint16(p, 0x010e, true)
  dv.setUint16(p + 2, 2, true) // ASCII
  dv.setUint32(p + 4, desc.length, true)
  dv.setUint32(p + 8, descOffset, true)
  p += 12

  // Software 0x0131 — ASCII
  dv.setUint16(p, 0x0131, true)
  dv.setUint16(p + 2, 2, true)
  dv.setUint32(p + 4, soft.length, true)
  dv.setUint32(p + 8, softOffset, true)
  p += 12

  dv.setUint32(p, 0, true) // next IFD
  tiff.set(soft, softOffset)
  tiff.set(desc, descOffset)

  const exifHeader = encodeLatin1('Exif\0\0')
  const app1Payload = concatBytes([exifHeader, tiff])
  const app1Len = app1Payload.length + 2
  if (app1Len > 0xffff) throw new Error('JPEG EXIF too large')
  const out = new Uint8Array(2 + 2 + app1Payload.length)
  out[0] = 0xff
  out[1] = 0xe1
  out[2] = (app1Len >> 8) & 0xff
  out[3] = app1Len & 0xff
  out.set(app1Payload, 4)
  return out
}

/**
 * Insert EXIF Software/ImageDescription + COM SourceURL after SOI.
 * Leaves entropy-coded scan untouched (no re-encode).
 */
export function injectJpegMetadata(bytes: Uint8Array): Uint8Array {
  if (!isJpeg(bytes)) throw new Error('injectJpegMetadata: not a JPEG')
  // Strip any prior APP1 Exif we inserted + COM that start with our markers,
  // so re-inject is idempotent. Keep JFIF APP0 and everything else.
  const segments: Uint8Array[] = []
  let off = 2 // after SOI
  while (off + 1 < bytes.length) {
    if (bytes[off] !== 0xff) {
      // Start of entropy-coded data (or padding) — keep rest verbatim.
      segments.push(bytes.subarray(off))
      off = bytes.length
      break
    }
    // Skip fill 0xFF bytes
    while (off < bytes.length && bytes[off] === 0xff && off + 1 < bytes.length && bytes[off + 1] === 0xff) {
      off++
    }
    if (off + 1 >= bytes.length) break
    const marker = bytes[off + 1]
    // Standalone markers without length
    if (marker === 0xd9 /* EOI */ || marker === 0x01 || (marker >= 0xd0 && marker <= 0xd7)) {
      segments.push(bytes.subarray(off, off + 2))
      off += 2
      continue
    }
    if (marker === 0xda /* SOS */) {
      // SOS + rest of file (scan) — keep all
      segments.push(bytes.subarray(off))
      off = bytes.length
      break
    }
    if (off + 4 > bytes.length) throw new Error('injectJpegMetadata: truncated segment')
    const segLen = (bytes[off + 2] << 8) | bytes[off + 3]
    const segEnd = off + 2 + segLen
    if (segEnd > bytes.length) throw new Error('injectJpegMetadata: truncated segment body')
    const body = bytes.subarray(off + 4, segEnd)
    let skip = false
    if (marker === 0xe1) {
      // Skip existing Exif APP1 so we replace with ours (canvas JPEG has none).
      if (
        body.length >= 6 &&
        body[0] === 0x45 &&
        body[1] === 0x78 &&
        body[2] === 0x69 &&
        body[3] === 0x66 &&
        body[4] === 0x00 &&
        body[5] === 0x00
      ) {
        skip = true
      }
    }
    if (marker === 0xfe) {
      const text = String.fromCharCode(...body)
      if (
        text.startsWith('Software:') ||
        text.startsWith('Description:') ||
        text.startsWith('SourceURL:') ||
        text === VISTOERA_SOFTWARE ||
        text === VISTOERA_DESCRIPTION ||
        text === VISTOERA_SOURCE_URL
      ) {
        skip = true
      }
    }
    if (!skip) segments.push(bytes.subarray(off, segEnd))
    off = segEnd
  }

  const injected = [
    buildJpegExifApp1(VISTOERA_SOFTWARE, VISTOERA_DESCRIPTION),
    buildJpegCom(`SourceURL:${VISTOERA_SOURCE_URL}`),
    buildJpegCom(`Software:${VISTOERA_SOFTWARE}`),
    buildJpegCom(`Description:${VISTOERA_DESCRIPTION}`),
  ]
  const soi = bytes.subarray(0, 2)
  return concatBytes([soi, ...injected, ...segments])
}

/** Best-effort JPEG COM + EXIF Software readback for tests. */
export function readJpegMetadataHints(bytes: Uint8Array): {
  software: string | null
  description: string | null
  sourceUrl: string | null
  com: string[]
} {
  if (!isJpeg(bytes)) throw new Error('readJpegMetadataHints: not a JPEG')
  const com: string[] = []
  let software: string | null = null
  let description: string | null = null
  let sourceUrl: string | null = null
  let off = 2
  while (off + 3 < bytes.length) {
    if (bytes[off] !== 0xff) break
    while (off < bytes.length && bytes[off] === 0xff && off + 1 < bytes.length && bytes[off + 1] === 0xff) off++
    if (off + 1 >= bytes.length) break
    const marker = bytes[off + 1]
    if (marker === 0xda || marker === 0xd9) break
    if (marker === 0x01 || (marker >= 0xd0 && marker <= 0xd7)) {
      off += 2
      continue
    }
    const segLen = (bytes[off + 2] << 8) | bytes[off + 3]
    const segEnd = off + 2 + segLen
    if (segEnd > bytes.length) break
    const body = bytes.subarray(off + 4, segEnd)
    if (marker === 0xfe) {
      const text = String.fromCharCode(...body)
      com.push(text)
      if (text.startsWith('SourceURL:')) sourceUrl = text.slice('SourceURL:'.length)
      if (text.startsWith('Software:')) software = text.slice('Software:'.length)
      if (text.startsWith('Description:')) description = text.slice('Description:'.length)
    }
    if (marker === 0xe1 && body.length > 14 && String.fromCharCode(...body.subarray(0, 4)) === 'Exif') {
      // Minimal LE IFD0 ASCII Software / ImageDescription parse
      const tiff = body.subarray(6)
      if (tiff[0] === 0x49 && tiff[1] === 0x49) {
        const dv = new DataView(tiff.buffer, tiff.byteOffset, tiff.byteLength)
        const ifd0 = dv.getUint32(4, true)
        if (ifd0 + 2 <= tiff.length) {
          const n = dv.getUint16(ifd0, true)
          for (let i = 0; i < n; i++) {
            const e = ifd0 + 2 + i * 12
            if (e + 12 > tiff.length) break
            const tag = dv.getUint16(e, true)
            const type = dv.getUint16(e + 2, true)
            const count = dv.getUint32(e + 4, true)
            const valOff = dv.getUint32(e + 8, true)
            if (type !== 2 || count === 0 || valOff + count > tiff.length) continue
            const raw = String.fromCharCode(...tiff.subarray(valOff, valOff + count)).replace(/\0+$/, '')
            if (tag === 0x0131) software = raw
            if (tag === 0x010e) description = raw
          }
        }
      }
    }
    off = segEnd
  }
  return { software, description, sourceUrl, com }
}

/**
 * Inject Vistoera source metadata into an editor export Blob.
 * PNG → tEXt; JPEG → EXIF + COM. Other types returned unchanged.
 * Throws if PNG/JPEG structure is invalid after attempted write.
 */
export async function injectVistoeraExportMetadata(blob: Blob): Promise<Blob> {
  const type = (blob.type || '').toLowerCase()
  const bytes = new Uint8Array(await blob.arrayBuffer())
  if (type === 'image/png' || (!type && isPng(bytes)) || (type.includes('png') && isPng(bytes))) {
    if (!isPng(bytes)) throw new Error('Vistoera metadata: claimed PNG is invalid')
    const out = injectPngTextMetadata(bytes)
    // Integrity: signature + IHDR + IEND still present; text readable.
    const readback = readPngTextMetadata(out)
    if (readback.Software !== VISTOERA_SOFTWARE) {
      throw new Error('Vistoera metadata: PNG Software write verification failed')
    }
    if (readback.Source !== VISTOERA_SOURCE_URL) {
      throw new Error('Vistoera metadata: PNG Source write verification failed')
    }
    if (readback.Description !== VISTOERA_DESCRIPTION) {
      throw new Error('Vistoera metadata: PNG Description write verification failed')
    }
    return new Blob([toArrayBuffer(out)], { type: 'image/png' })
  }
  if (
    type === 'image/jpeg' ||
    type === 'image/jpg' ||
    (!type && isJpeg(bytes)) ||
    ((type.includes('jpeg') || type.includes('jpg')) && isJpeg(bytes))
  ) {
    if (!isJpeg(bytes)) throw new Error('Vistoera metadata: claimed JPEG is invalid')
    const out = injectJpegMetadata(bytes)
    const hints = readJpegMetadataHints(out)
    if (hints.software !== VISTOERA_SOFTWARE) {
      throw new Error('Vistoera metadata: JPEG Software write verification failed')
    }
    if (hints.sourceUrl !== VISTOERA_SOURCE_URL) {
      throw new Error('Vistoera metadata: JPEG SourceURL write verification failed')
    }
    return new Blob([toArrayBuffer(out)], { type: 'image/jpeg' })
  }
  // WebP / unknown — editor save path does not emit these today.
  return blob
}
